import { SxProps, Theme, alpha } from '@mui/material';
import { Color } from 'shared/utils/colors';

import { fieldLabelSx } from 'shared/styles/typography';

export const richTextRootSx: SxProps<Theme> = {
  mt: 3,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};

export const richTextRootDenseSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};

/** Matches MuiOutlinedInput / form fields (`4px`). */
const RICH_TEXT_BORDER_RADIUS = '4px';

export const labelAboveFieldSx = (
  error?: boolean,
  focused?: boolean
): SxProps<Theme> => ({
  ...fieldLabelSx,
  color: error
    ? 'error.main'
    : focused
      ? Color.primaryOrangeDark
      : 'text.secondary',
  transition: (theme) =>
    theme.transitions.create('color', {
      duration: theme.transitions.duration.shortest,
    }),
});

export const editorWrapperSx = (error?: boolean, focused?: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: (theme) => {
    if (error) {
      return theme.palette.error.main;
    }
    if (focused) {
      return Color.primaryOrange;
    }
    return alpha(
      theme.palette.mode === 'light'
        ? theme.palette.common.black
        : theme.palette.common.white,
      0.23
    );
  },
  // Extra 1px ring on focus (2px total) without changing box size — matches MuiOutlinedInput.
  boxShadow: (theme) => {
    if (!focused) {
      return 'none';
    }
    const ringColor = error ? theme.palette.error.main : Color.primaryOrange;
    return `0 0 0 1px ${ringColor}`;
  },
  borderRadius: RICH_TEXT_BORDER_RADIUS,
  backgroundColor: 'background.paper',
  overflow: 'hidden',
  transition: (theme) =>
    theme.transitions.create(['border-color', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
  '&:hover': {
    borderColor: (theme) => {
      if (error) {
        return theme.palette.error.main;
      }
      if (focused) {
        return Color.primaryOrange;
      }
      return theme.palette.text.primary;
    },
  },
});

/** Icon size of the toolbar buttons. */
export const TOOLBAR_ICON_FONT_SIZE = '1.1rem';
/** Padding of a small toolbar `IconButton` on each side. */
export const TOOLBAR_BUTTON_PADDING_PX = 5;
/** Height of a small toolbar `IconButton`: the icon and its padding. */
export const TOOLBAR_BUTTON_HEIGHT = `calc(${TOOLBAR_ICON_FONT_SIZE} + ${2 * TOOLBAR_BUTTON_PADDING_PX}px)`;

export const toolbarIconSx: SxProps<Theme> = {
  fontSize: TOOLBAR_ICON_FONT_SIZE,
};

/** Between groups of the toolbar: formatting, colour, insert, lists, line break. */
export const toolbarDividerSx: SxProps<Theme> = {
  mx: 0.5,
};

export const toolbarSx = (error?: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexWrap: 'wrap',
  // Buttons of different shapes (the colour pill) stay on one line with the icons.
  alignItems: 'center',
  gap: 0.5,
  padding: 1,
  backgroundColor: 'background.paper',
  borderBottom: 1,
  borderColor: (theme) => alpha(theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white, 0.23),
});

export const editorContentSx = (
  error?: boolean,
  disabled?: boolean,
  minRows = 3,
  autoGrow = false
): SxProps<Theme> => ({
  padding: 2,
  ...(autoGrow ? { minHeight: 0 } : { minHeight: `${minRows * 1.5}em` }),
  backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
  cursor: disabled ? 'not-allowed' : 'text',
  '& .ProseMirror': {
    outline: 'none',
    ...(autoGrow
      ? {
          minHeight: `${minRows * 1.5}em`,
          height: 'auto',
        }
      : { minHeight: `${minRows * 1.5}em` }),
    // Only on an empty editor: TipTap also marks the empty line under the
    // cursor (e.g. after Enter) as `is-empty`, where a hint is just noise.
    '& p.is-editor-empty:first-child::before': {
      color: 'text.disabled',
      content: 'attr(data-placeholder)',
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },
    '& p': {
      margin: 0,
      marginBottom: 1,
    },
    ...(autoGrow
      ? {
          '& p:last-child': {
            marginBottom: 0,
          },
        }
      : {}),
    '& ul, & ol': {
      paddingLeft: 3,
    },
    '& code': {
      backgroundColor: 'action.hover',
      padding: '2px 4px',
      borderRadius: '4px',
      fontFamily: 'monospace',
    },
    '& a': {
      // Darkened primary for legible link text on light surfaces (WCAG 1.4.3).
      color: Color.primaryOrangeDark,
      textDecoration: 'underline',
    },
  },
});

export const helperTextSx = (error?: boolean): SxProps<Theme> => ({
  fontSize: '0.75rem',
  marginTop: 0.5,
  marginRight: 1,
  marginBottom: 0.5,
  paddingX: 0.5,
  backgroundColor: 'background.paper',
  textAlign: 'right',
  color: error ? 'error.main' : 'text.secondary',
});
